/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ComponentProps,
    type KeyboardEvent,
} from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

/**
 * Figma-driven horizontal carousel primitives.
 *
 * This file is a drop-in replacement for `@/components/ui/carousel` — it re-exports
 * the same building blocks (`Carousel`, `CarouselContent`, `CarouselItem`,
 * `CarouselPrevious`, `CarouselNext`, `CarouselApi`) with refreshed styling that
 * uses Tailwind utilities + design tokens from `src/theme/`.
 *
 * Only horizontal orientation is supported, per the current Figma spec.
 *
 * Size variants (matching Figma):
 * - `Large`  — single 320×320 card per viewport, side arrows, pagination label.
 * - `Small`  — compact cards, multiple visible per viewport. On mobile
 *              (below the `md` breakpoint) each item enforces a 256×256
 *              minimum (`min-w-64 min-h-64`) so product-tile images render
 *              as a proper square per Figma cart-recs mobile spec; desktop
 *              keeps the existing consumer-driven sizing.
 * - `middle` — 288×396 "peek" cards: the first slide is fully visible, the next
 *              peeks in, and clicking the top-right chevron snaps it into view.
 *              (Replaces the previous `Small_v2` variant.)
 */
export type CarouselSize = 'Large' | 'Small' | 'middle';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    /** Kept for API compatibility with the legacy primitive. Only `horizontal` is supported. */
    orientation?: 'horizontal';
    /** Figma-driven visual size variant. Defaults to `Large`. */
    size?: CarouselSize;
    setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    size: CarouselSize;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
    const context = useContext(CarouselContext);
    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />');
    }
    return context;
}

function Carousel({
    opts,
    setApi,
    plugins,
    size = 'Large',
    className,
    children,
    ...props
}: ComponentProps<'div'> & CarouselProps) {
    const [carouselRef, api] = useEmblaCarousel(
        {
            ...opts,
            axis: 'x',
            // `Large` centers the lone card in the viewport; the other variants
            // align to the start so the first card is flush-left and subsequent
            // cards peek in from the right (key to the `middle` UX).
            align: opts?.align ?? (size === 'Large' ? 'center' : 'start'),
            containScroll: opts?.containScroll ?? 'trimSnaps',
        },
        plugins
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const onSelect = useCallback((emblaApi: CarouselApi) => {
        if (!emblaApi) return;
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
    }, []);

    const scrollPrev = useCallback(() => {
        api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
        api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                scrollPrev();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                scrollNext();
            }
        },
        [scrollPrev, scrollNext]
    );

    useEffect(() => {
        if (!api || !setApi) return;
        setApi(api);
    }, [api, setApi]);

    useEffect(() => {
        if (!api) return;
        onSelect(api);
        api.on('reInit', onSelect);
        api.on('select', onSelect);

        return () => {
            api?.off('select', onSelect);
        };
    }, [api, onSelect]);

    return (
        <CarouselContext.Provider
            value={{
                carouselRef,
                api,
                opts,
                orientation: 'horizontal',
                size,
                scrollPrev,
                scrollNext,
                canScrollPrev,
                canScrollNext,
            }}>
            <div
                onKeyDownCapture={handleKeyDown}
                className={cn('relative', className)}
                role="region"
                aria-roledescription="carousel"
                data-slot="carousel"
                data-size={size}
                {...props}>
                {children}
            </div>
        </CarouselContext.Provider>
    );
}

// Figma-driven per-variant inter-item spacing.
// Source: Figma frames `25477:91359` / `25477:91361`.
//   - Large  →  0px  (single card per viewport — no inter-card gutter)
//   - Small  → 16px  (var(--p-4))
//   - middle → 24px  (var(--p-6) / --image-carousel/spacing-size/width)
//
// Implementation note: Embla's recommended gutter pattern offsets the track by
// a negative left margin and pads each item by the same amount, so the first
// card is flush-left while subsequent cards have a visible gap. For the Large
// variant we set both to 0 (the cards don't need a gutter because only one is
// visible at a time and the rest are scrolled in).
const GAP_BY_SIZE: Record<CarouselSize, { track: string; item: string }> = {
    Large: { track: '', item: '' },
    Small: { track: '-pr-4', item: 'pr-4' },
    middle: { track: '-pr-6', item: 'pr-6' },
};

function CarouselContent({ className, ...props }: ComponentProps<'div'>) {
    const { carouselRef, size } = useCarousel();
    const { track } = GAP_BY_SIZE[size];

    return (
        <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
            <div className={cn('flex', track, className)} {...props} />
        </div>
    );
}

// Figma cart-recs mobile spec — Small carousel items are at least 256×256
// (`w-64`/`h-64` in Tailwind's default scale) below the `md` breakpoint so
// product-tile images render as a proper square. Applied only for `Small`
// because `Large` centers a single card per viewport and `middle` already
// declares its own peek dimensions. Desktop is untouched — consumer classes
// keep control from `md` up.
const MOBILE_SQUARE_MIN_BY_SIZE: Record<CarouselSize, string> = {
    Large: '',
    Small: 'max-md:min-w-64 max-md:min-h-64',
    middle: '',
};

function CarouselItem({ className, ...props }: ComponentProps<'div'>) {
    const { size } = useCarousel();
    const { item } = GAP_BY_SIZE[size];
    const mobileSquareMin = MOBILE_SQUARE_MIN_BY_SIZE[size];

    return (
        <div
            role="group"
            aria-roledescription="slide"
            data-slot="carousel-item"
            className={cn('min-w-0 shrink-0 grow-0 basis-full', mobileSquareMin, item, className)}
            {...props}
        />
    );
}

/**
 * Default side-arrow button used for `Large` / `Small` variants.
 *
 * Figma variant: `outline` / `size="icon-sm"` (32×32) with `rounded-full` and
 * `shadow-2xs`. The base `@/components/buttons` Button renders labels in
 * uppercase with tracking by default; those rules don't apply to icon-only
 * nav buttons, so we reset them here.
 */
function SideArrowButton({
    className,
    direction,
    ...props
}: ComponentProps<typeof Button> & { direction: 'prev' | 'next' }) {
    return (
        <Button
            variant="outline"
            size="icon-sm"
            className={cn(
                'absolute top-1/2 -translate-y-1/2 rounded-full normal-case tracking-normal shadow-2xs disabled:opacity-50',
                direction === 'prev' ? '-left-12' : '-right-12',
                className
            )}
            {...props}>
            {direction === 'prev' ? <ArrowLeft /> : <ArrowRight />}
            <span className="sr-only">{direction === 'prev' ? 'Previous slide' : 'Next slide'}</span>
        </Button>
    );
}

/** Outline chevron button used by the `middle` carousel navigation cluster. */
function MiddleChevronButton({
    className,
    direction,
    ...props
}: ComponentProps<typeof Button> & { direction: 'prev' | 'next' }) {
    return (
        <Button
            variant="outline"
            size="icon-sm"
            className={cn('rounded-full normal-case tracking-normal shadow-2xs disabled:opacity-50', className)}
            {...props}>
            {direction === 'prev' ? <ChevronLeft /> : <ChevronRight />}
            <span className="sr-only">{direction === 'prev' ? 'Previous slide' : 'Next slide'}</span>
        </Button>
    );
}

/**
 * The `middle` variant anchors its prev/next buttons as a pair in the top-right.
 * We render the pair from whichever of `CarouselPrevious` / `CarouselNext` is
 * mounted first, so consumers can keep using the same compositional API.
 */
function MiddleNavCluster() {
    const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();
    return (
        <div data-slot="carousel-middle-nav" className="absolute -top-[52px] right-0 flex items-center gap-2">
            <MiddleChevronButton
                direction="prev"
                data-slot="carousel-previous"
                onClick={scrollPrev}
                disabled={!canScrollPrev}
            />
            <MiddleChevronButton
                direction="next"
                data-slot="carousel-next"
                onClick={scrollNext}
                disabled={!canScrollNext}
            />
        </div>
    );
}

function CarouselPrevious({ className, ...props }: ComponentProps<typeof Button>) {
    const { size, scrollPrev, canScrollPrev } = useCarousel();

    // For `middle`, the prev button is rendered as part of the top-right
    // cluster in `CarouselNext` (see below). Returning the cluster here too
    // would double-render it, so we no-op when `middle` + `prev`.
    if (size === 'middle') return null;

    return (
        <SideArrowButton
            data-slot="carousel-previous"
            direction="prev"
            className={className}
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            {...props}
        />
    );
}

function CarouselNext({ className, ...props }: ComponentProps<typeof Button>) {
    const { size, scrollNext, canScrollNext } = useCarousel();

    if (size === 'middle') {
        return <MiddleNavCluster />;
    }

    return (
        <SideArrowButton
            data-slot="carousel-next"
            direction="next"
            className={className}
            disabled={!canScrollNext}
            onClick={scrollNext}
            {...props}
        />
    );
}

export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };
